import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

type CdrRow = {
  id: string;
  callId?: string | null;
  direction: string;
  status: string;
  extension: string;
  customer: string;
  agentName: string;
  startedAt?: string | null;
  answeredAt?: string | null;
  endedAt?: string | null;
  talkSec: number;
  callSec: number;
  hangupCause: string;
  outcome: "answered" | "missed" | string;
};

type UserStat = {
  extension: string;
  agentName: string;
  total: number;
  answered: number;
  missed: number;
  talkSec: number;
  callSec: number;
};

type Report = {
  days: number;
  summary: {
    total: number;
    answered: number;
    missed: number;
    other: number;
    talkSec: number;
    callSec: number;
    avgTalkSec: number;
    answerRate: number;
    byUser: UserStat[];
  };
  calls: CdrRow[];
};

function fmtDur(sec: number) {
  const s = Math.max(0, Math.round(Number(sec) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${r}s`;
  return `${r}s`;
}

function fmtWhen(value?: string | null) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function BarChart({
  title,
  items,
  valueKey,
  format = (n: number) => String(n),
  color = "#0f8f7a",
}: {
  title: string;
  items: UserStat[];
  valueKey: keyof UserStat;
  format?: (n: number) => string;
  color?: string;
}) {
  const max = Math.max(1, ...items.map((i) => Number(i[valueKey]) || 0));
  return (
    <section className="panel cdr-chart-panel">
      <div className="panel-head">
        <h3>{title}</h3>
      </div>
      {items.length === 0 ? (
        <p className="muted cdr-empty">No data in this range.</p>
      ) : (
        <div className="cdr-bars">
          {items.map((row) => {
            const value = Number(row[valueKey]) || 0;
            const pct = Math.round((value / max) * 100);
            return (
              <div key={`${title}-${row.extension}`} className="cdr-bar-row">
                <div className="cdr-bar-label">
                  <b>{row.agentName}</b>
                  <span className="muted">{row.extension}</span>
                </div>
                <div className="cdr-bar-track">
                  <div className="cdr-bar-fill" style={{ width: `${pct}%`, background: color }} />
                </div>
                <div className="cdr-bar-value">{format(value)}</div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

export default function CdrPage() {
  const [days, setDays] = useState(7);
  const [report, setReport] = useState<Report | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState("");

  async function load(range = days) {
    setBusy(true);
    setError("");
    try {
      const data = await api<Report>(`/cdr/report?days=${range}`);
      setReport(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load CDR");
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    void load(days);
  }, [days]);

  const summary = report?.summary;
  const byUser = summary?.byUser || [];

  const visibleCalls = useMemo(() => {
    const rows = report?.calls || [];
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (c) =>
        c.customer.includes(q) ||
        c.extension.includes(q) ||
        (c.agentName || "").toLowerCase().includes(q) ||
        (c.outcome || "").toLowerCase().includes(q) ||
        (c.hangupCause || "").toLowerCase().includes(q),
    );
  }, [report, query]);

  return (
    <div className="cdr-page">
      <div className="ext-page-head">
        <div>
          <h1>CDR report</h1>
          <p className="muted">Call detail records, talk time, and agent performance</p>
        </div>
        <div className="ext-page-tools">
          <select
            value={days}
            disabled={busy}
            onChange={(e) => setDays(Number(e.target.value))}
          >
            <option value={1}>Last 24 hours</option>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <button className="btn ghost" type="button" disabled={busy} onClick={() => void load(days)}>
            {busy ? "Loading…" : "Refresh"}
          </button>
        </div>
      </div>

      {error ? <p className="error">{error}</p> : null}

      <div className="cdr-kpis">
        <div className="stat-card">
          <span>Total calls</span>
          <b>{summary?.total ?? "—"}</b>
        </div>
        <div className="stat-card">
          <span>Answered</span>
          <b>{summary?.answered ?? "—"}</b>
        </div>
        <div className="stat-card">
          <span>Not picked / missed</span>
          <b className="warn">{summary?.missed ?? "—"}</b>
        </div>
        <div className="stat-card">
          <span>Answer rate</span>
          <b>{summary ? `${summary.answerRate}%` : "—"}</b>
        </div>
        <div className="stat-card">
          <span>Talk time</span>
          <b>{summary ? fmtDur(summary.talkSec) : "—"}</b>
        </div>
        <div className="stat-card">
          <span>Avg talk</span>
          <b>{summary ? fmtDur(summary.avgTalkSec) : "—"}</b>
        </div>
      </div>

      <div className="cdr-charts">
        <BarChart title="Calls by agent" items={byUser} valueKey="total" color="#0b2540" />
        <BarChart title="Talk time by agent" items={byUser} valueKey="talkSec" format={fmtDur} color="#0f8f7a" />
        <BarChart title="Missed / not picked" items={byUser} valueKey="missed" color="#c45c4a" />
      </div>

      <section className="panel cdr-user-panel">
        <div className="panel-head">
          <h3>Agent summary</h3>
          <span className="muted">{byUser.length} desks</span>
        </div>
        <table className="table ext-table">
          <thead>
            <tr>
              <th>Agent</th>
              <th>Ext</th>
              <th>Calls</th>
              <th>Answered</th>
              <th>Missed</th>
              <th>Talk time</th>
              <th>Call time</th>
            </tr>
          </thead>
          <tbody>
            {byUser.length === 0 && (
              <tr>
                <td colSpan={7} className="muted">
                  No CDR rows in this period yet.
                </td>
              </tr>
            )}
            {byUser.map((u) => (
              <tr key={u.extension}>
                <td>
                  <b>{u.agentName}</b>
                </td>
                <td className="ext-read">{u.extension}</td>
                <td>{u.total}</td>
                <td>{u.answered}</td>
                <td>{u.missed}</td>
                <td>{fmtDur(u.talkSec)}</td>
                <td>{fmtDur(u.callSec)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="panel ext-panel">
        <div className="users-toolbar cdr-toolbar">
          <div>
            <h3 className="cdr-table-title">Call detail</h3>
            <p className="muted">{visibleCalls.length} rows</p>
          </div>
          <input
            className="search"
            placeholder="Search number, agent, status…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <table className="table ext-table">
          <thead>
            <tr>
              <th>When</th>
              <th>Dir</th>
              <th>Agent</th>
              <th>Ext</th>
              <th>Customer</th>
              <th>Outcome</th>
              <th>Talk</th>
              <th>Call</th>
              <th>Cause</th>
            </tr>
          </thead>
          <tbody>
            {visibleCalls.length === 0 && (
              <tr>
                <td colSpan={9} className="muted">
                  No calls match this filter.
                </td>
              </tr>
            )}
            {visibleCalls.map((c) => (
              <tr key={c.id}>
                <td>{fmtWhen(c.startedAt)}</td>
                <td>
                  <span className="pill">{c.direction || "—"}</span>
                </td>
                <td>{c.agentName || "—"}</td>
                <td className="ext-read">{c.extension || "—"}</td>
                <td>{c.customer || "—"}</td>
                <td>
                  <span
                    className={`pill ${
                      c.outcome === "answered" ? "live" : c.outcome === "missed" ? "dead" : "idle"
                    }`}
                  >
                    {c.outcome}
                  </span>
                </td>
                <td>{fmtDur(c.talkSec)}</td>
                <td>{fmtDur(c.callSec)}</td>
                <td className="muted">{c.hangupCause || c.status || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}
